/**
 * Zod schema for "Add GitLab Connection" (Charter M3 migration), imported by
 * `GitLabConnectionForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs `VGL-*`
 * mirror the pre-migration hand-rolled `validate()` exactly.
 */
import { z } from 'zod';
import type { GitLabConnection, CreateGitLabConnectionRequest } from '../types';
import { GITLAB_AUTH_TYPE_OPTIONS, isParseableUrl } from './connectionFormFields';

// reason: GITLAB_AUTH_TYPE_OPTIONS is a runtime-verified non-empty array of the exported union
// type; zod's z.enum() requires a literal non-empty tuple type.
const AUTH_TYPE_TUPLE = GITLAB_AUTH_TYPE_OPTIONS.map((o) => o.value) as [
  GitLabConnection['authType'],
  ...GitLabConnection['authType'][],
];

export const GitLabConnectionSchema = z.object({
  // VGL-01
  name: z.string().trim().min(1, 'Connection name is required'),
  // VGL-02 (required) / VGL-03 (must be a parseable URL)
  instanceUrl: z
    .string()
    .trim()
    .min(1, 'Instance URL is required')
    .refine(isParseableUrl, { message: 'Please enter a valid URL' }),
  authType: z.enum(AUTH_TYPE_TUPLE),
  // VGL-04
  accessToken: z.string().trim().min(1, 'Access token is required'),
});

/** The shape RHF's `useForm<T>`/`defaultValues` are typed against (pre-transform). */
export type GitLabConnectionFormValues = z.input<typeof GitLabConnectionSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed). */
export type GitLabConnectionFormOutput = z.output<typeof GitLabConnectionSchema>;

// Compile-time proof the schema's output assembles a valid request DTO.
type _AssertAssignable = GitLabConnectionFormOutput extends CreateGitLabConnectionRequest ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
